import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './PickupDetails.css';

const PickupDetails = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { pickups, setCurrentPickup } = useApp();
  const [pickup, setPickup] = useState(null);

  useEffect(() => {
    const foundPickup = pickups.find(p => p.id === orderId);
    if (foundPickup) {
      setPickup(foundPickup);
      setCurrentPickup(foundPickup);
    } else {
      navigate('/dashboard');
    }
  }, [orderId, pickups, setCurrentPickup, navigate]);

  if (!pickup) {
    return (
      <div className="pd-screen">
        <div className="pd-loading">
          <div className="pd-spinner"></div>
        </div>
      </div>
    );
  }

  const handleStart = () => navigate(`/pickup/${orderId}/process`);
  
  const handleDirections = () => {
    const address = getFullAddress();
    const url = pickup.location?.lat && pickup.location?.lng 
      ? `https://www.google.com/maps/dir/?api=1&destination=${pickup.location.lat},${pickup.location.lng}`
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  const handleCall = () => {
    if (pickup.customer?.phone) {
      window.location.href = `tel:${pickup.customer.phone}`;
    }
  };

  const getFullAddress = () => {
    const parts = [
      pickup.location?.address,
      pickup.location?.city,
      pickup.location?.pincode,
      pickup.location?.state,
      pickup.location?.country
    ].filter(Boolean);
    return parts.join(', ') || 'Address not available';
  };

  const getProducts = () => {
    if (pickup.items?.length > 0) {
      return pickup.items.map(item => item.product?.product_name || item.product_name || 'Product').join(', ');
    }
    return pickup.type || 'Plastic';
  };

  const isCompleted = ['PAID', 'COMPLETED'].includes(pickup.status);

  return (
    <div className="pd-screen">
      {/* Header */}
      <header className="pd-header">
        <button className="pd-back" onClick={() => navigate('/dashboard')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="pd-title">Order #{orderId}</h1>
        <div style={{ width: 36 }}></div>
      </header>

      <div className="pd-body">
        {/* Map Card */}
        <div className="pd-map">
          <div className="pd-map-bg">
            <div className="pd-pin-wrap">
              <div className="pd-pulse"></div>
              <div className="pd-pin">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
            </div>
            <span className="pd-city">{pickup.location?.city || pickup.location?.state || 'Location'}</span>
          </div>
        </div>

        {/* Address */}
        <section className="pd-section">
          <div className="pd-label">
            <span className="pd-dot red"></span>
            Address
          </div>
          <p className="pd-text">{getFullAddress()}</p>
        </section>

        {/* Customer */}
        <section className="pd-section">
          <div className="pd-label">
            <span className="pd-dot blue"></span>
            Customer
          </div>
          <div className="pd-customer">
            <div className="pd-avatar">
              {pickup.customer?.name?.charAt(0)?.toUpperCase() || 'C'}
            </div>
            <div className="pd-info">
              <span className="pd-name">{pickup.customer?.name || 'Customer'}</span>
              <span className="pd-phone">{pickup.customer?.phone || 'No phone'}</span>
            </div>
            <button 
              className="pd-call" 
              onClick={handleCall}
              disabled={!pickup.customer?.phone}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
            </button>
          </div>
        </section>

        {/* Schedule */}
        {pickup.schedule && (pickup.schedule.period || pickup.schedule.date || pickup.schedule.dateFull) && (
          <section className="pd-section">
            <div className="pd-label">
              <span className="pd-dot purple"></span>
              Schedule
            </div>
            <div className="pd-schedule">
              {/* First line: Day, Date */}
              {(pickup.schedule.dateFull || pickup.schedule.date) && (
                <div className="pd-schedule-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>
                    {(() => {
                      // Try to parse dateFull first (format: "Monday, January 25, 2026")
                      if (pickup.schedule.dateFull) {
                        // Extract full date: "January 25, 2026" from "Monday, January 25, 2026"
                        const parts = pickup.schedule.dateFull.split(', ');
                        if (parts.length >= 2) {
                          // Skip the day (parts[0]) and get the date part
                          const datePart = parts.slice(1).join(', '); // "January 25, 2026"
                          return datePart;
                        }
                        // Fallback: try parsing as Date
                        const date = new Date(pickup.schedule.dateFull);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });
                        }
                        return pickup.schedule.dateFull;
                      }
                      // Fallback to date if dateFull not available
                      if (pickup.schedule.date) {
                        // Try to parse and format
                        const date = new Date(pickup.schedule.date);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });
                        }
                        return pickup.schedule.date;
                      }
                      return '';
                    })()}
                  </span>
                </div>
              )}
              {/* Second line: Schedule Period */}
              {pickup.schedule.period && (
                <div className="pd-schedule-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  <span>
                    {(() => {
                      // Keep period in normal case (not uppercase)
                      const period = pickup.schedule.period;
                      // Check if period already contains time
                      const hasTime = /(AM|PM)/i.test(pickup.schedule.period);
                      
                      if (hasTime) {
                        // Period already has time, return as is
                        return period;
                      } else {
                        // Format as "Afternoon 2 pm - 4 pm"
                        const startTime = pickup.schedule.start ? pickup.schedule.start.toLowerCase().replace(/\s+(am|pm)/g, '$1') : '';
                        const endTime = pickup.schedule.end ? pickup.schedule.end.toLowerCase().replace(/\s+(am|pm)/g, '$1') : '';
                        const timeRange = startTime && endTime ? ` ${startTime} - ${endTime}` : '';
                        return `${period}${timeRange}`;
                      }
                    })()}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Instructions */}
        {pickup.instructions && (
          <section className="pd-section">
            <div className="pd-label">
              <span className="pd-dot yellow"></span>
              Note
            </div>
            <div className="pd-note">{pickup.instructions}</div>
          </section>
        )}

        {/* Actions */}
        <div className="pd-actions">
          {isCompleted ? (
            <div className="pd-done">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <path d="M22 4L12 14.01l-3-3"/>
              </svg>
              Completed
            </div>
          ) : (
            <>
              <button className="pd-primary" onClick={handleStart}>
                Start Collection
              </button>
              <div className="pd-row">
                <button className="pd-secondary" onClick={handleDirections}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="3,11 22,2 13,21 11,13 3,11"/>
                  </svg>
                  Directions
                </button>
                <button 
                  className="pd-secondary green" 
                  onClick={handleCall}
                  disabled={!pickup.customer?.phone}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  Call
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PickupDetails;
